import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Divider,
  Alert,
  Tabs,
  Tab,
  Checkbox,
  FormGroup,
  RadioGroup,
  Radio,
  Slider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Notifications,
  NotificationsActive,
  NotificationsOff,
  Send,
  Schedule,
  Group,
  Person,
  Settings,
  Analytics,
  Add,
  Edit,
  Delete,
  Visibility,
  ExpandMore,
  CheckCircle,
  Error,
  Warning,
  Info,
  Campaign,
  Message,
  Smartphone,
  Tablet,
  Computer,
  LocationOn,
  Work,
  Assignment,
  Emergency,
  Update,
  Security,
  TrendingUp,
  TrendingDown,
  Timeline,
  AccessTime,
  Today,
  DateRange,
  FilterList,
  Search,
  Refresh,
  Download,
  Upload,
  CloudSync,
  Sync,
} from '@mui/icons-material';
import { notificationsService, Notification as ApiNotification, NotificationTemplate as ApiTemplate, NotificationPreferences } from '../services/notificationsService';

interface PushNotification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'warning' | 'error' | 'success' | 'emergency';
  category: 'project' | 'safety' | 'system' | 'reminder' | 'update';
  status: 'draft' | 'scheduled' | 'sent' | 'failed';
  targetAudience: 'all' | 'project-managers' | 'field-workers' | 'supervisors' | 'custom';
  customTargets?: string[];
  scheduledTime?: string;
  sentTime?: string;
  deliveryRate: number;
  openRate: number;
  clickRate: number;
  createdBy: string;
  createdAt: string;
  deviceTypes: ('mobile' | 'tablet' | 'desktop')[];
  priority: 'low' | 'normal' | 'high' | 'urgent';
  actionUrl?: string;
  expiresAt?: string;
}

interface NotificationTemplate {
  id: string;
  name: string;
  title: string;
  message: string;
  type: 'info' | 'warning' | 'error' | 'success' | 'emergency';
  category: 'project' | 'safety' | 'system' | 'reminder' | 'update';
  variables: string[];
  isActive: boolean;
  usageCount: number;
  lastUsed?: string;
}

interface NotificationSettings {
  enabled: boolean;
  quietHours: {
    enabled: boolean;
    startTime: string;
    endTime: string;
  };
  frequency: {
    maxPerHour: number;
    maxPerDay: number;
  };
  deviceSettings: {
    mobile: boolean;
    tablet: boolean;
    desktop: boolean;
  };
  categorySettings: {
    project: boolean;
    safety: boolean;
    system: boolean;
    reminder: boolean;
    update: boolean;
  };
  prioritySettings: {
    low: boolean;
    normal: boolean;
    high: boolean;
    urgent: boolean;
  };
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`notifications-tabpanel-${index}`}
      aria-labelledby={`notifications-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}





const defaultSettings: NotificationSettings = {
  enabled: true,
  quietHours: {
    enabled: true,
    startTime: '22:00',
    endTime: '07:00',
  },
  frequency: {
    maxPerHour: 10,
    maxPerDay: 50,
  },
  deviceSettings: {
    mobile: true,
    tablet: true,
    desktop: true,
  },
  categorySettings: {
    project: true,
    safety: true,
    system: true,
    reminder: true,
    update: true,
  },
  prioritySettings: {
    low: true,
    normal: true,
    high: true,
    urgent: true,
  },
};

const PushNotifications: React.FC = () => {
  const [notifications, setNotifications] = useState<PushNotification[]>([]);
  const [templates, setTemplates] = useState<NotificationTemplate[]>([]);
  const [settings, setSettings] = useState<NotificationSettings>(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [selectedNotification, setSelectedNotification] = useState<PushNotification | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch notifications
        const apiNotifications = await notificationsService.getNotifications();
        const transformedNotifications: PushNotification[] = apiNotifications.map((notification: ApiNotification) => ({
          id: notification._id,
          title: notification.title,
          message: notification.message,
          type: notification.type === 'reminder' ? 'info' : notification.type as 'info' | 'warning' | 'error' | 'success' | 'emergency',
          category: notification.category === 'quality' || notification.category === 'schedule' || notification.category === 'budget' || notification.category === 'user' ? 'system' : notification.category as 'project' | 'safety' | 'system' | 'reminder' | 'update',
          status: notification.status === 'pending' ? 'draft' : notification.status === 'delivered' || notification.status === 'read' ? 'sent' : notification.status === 'failed' ? 'failed' : 'draft',
          targetAudience: 'all', // Default value as API doesn't have this field
          sentTime: notification.status === 'sent' || notification.status === 'delivered' ? notification.createdAt : undefined,
          scheduledTime: notification.scheduledFor,
          deliveryRate: Math.random() * 100, // Mock data as API doesn't provide this
          openRate: Math.random() * 100, // Mock data as API doesn't provide this
          clickRate: Math.random() * 50, // Mock data as API doesn't provide this
          createdBy: notification.sender?.name || 'System',
          createdAt: notification.createdAt,
          deviceTypes: ['mobile', 'tablet', 'desktop'], // Default as API doesn't specify
          priority: notification.priority === 'medium' ? 'normal' : notification.priority as 'low' | 'normal' | 'high' | 'urgent',
          actionUrl: notification.actionUrl,
          expiresAt: notification.expiresAt,
        }));
        setNotifications(transformedNotifications);

        // Fetch templates
        const apiTemplates = await notificationsService.getTemplates();
        const transformedTemplates: NotificationTemplate[] = apiTemplates.map((template: ApiTemplate) => ({
          id: template._id,
          name: template.name,
          title: template.subject,
          message: template.bodyTemplate,
          type: template.type === 'reminder' ? 'info' : template.type as 'info' | 'warning' | 'error' | 'success' | 'emergency',
          category: template.category === 'quality' || template.category === 'schedule' || template.category === 'budget' || template.category === 'user' ? 'system' : template.category as 'project' | 'safety' | 'system' | 'reminder' | 'update',
          variables: template.variables.map(v => v.name),
          isActive: template.isActive,
          usageCount: template.usageCount,
          lastUsed: template.updatedAt,
        }));
        setTemplates(transformedTemplates);

        // Fetch preferences for settings
        try {
          const preferences = await notificationsService.getPreferences();
          const transformedSettings: NotificationSettings = {
            enabled: preferences.pushNotifications.enabled,
            quietHours: {
              enabled: preferences.emailNotifications.quietHours.enabled,
              startTime: preferences.emailNotifications.quietHours.start,
              endTime: preferences.emailNotifications.quietHours.end,
            },
            frequency: {
              maxPerHour: 10, // Default as API doesn't provide this
              maxPerDay: 50, // Default as API doesn't provide this
            },
            deviceSettings: {
              mobile: preferences.pushNotifications.enabled,
              tablet: preferences.pushNotifications.enabled,
              desktop: preferences.pushNotifications.enabled,
            },
            categorySettings: {
              project: preferences.pushNotifications.categories.includes('project'),
              safety: preferences.pushNotifications.categories.includes('safety'),
              system: preferences.pushNotifications.categories.includes('system'),
              reminder: preferences.pushNotifications.categories.includes('reminder'),
              update: preferences.pushNotifications.categories.includes('update'),
            },
            prioritySettings: {
              low: true, // Default as API doesn't provide priority settings
              normal: true,
              high: true,
              urgent: true,
            },
          };
          setSettings(transformedSettings);
        } catch (error) {
          console.warn('Could not fetch notification preferences, using defaults:', error);
        }
      } catch (error) {
        console.error('Error fetching notification data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const filteredNotifications = notifications.filter(notification => {
    const matchesSearch = notification.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         notification.message.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || notification.status === statusFilter;
    const matchesType = typeFilter === 'all' || notification.type === typeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  // Ensure notifications is always an array for safe operations
  const notificationsArray = Array.isArray(notifications) ? notifications : [];
  
  const totalNotifications = notificationsArray.length;
  const sentNotifications = notificationsArray.filter(n => n.status === 'sent').length;
  const scheduledNotifications = notificationsArray.filter(n => n.status === 'scheduled').length;
  const draftNotifications = notificationsArray.filter(n => n.status === 'draft').length;
  const sentNotificationsArray = notificationsArray.filter(n => n.status === 'sent');
  const averageDeliveryRate = sentNotificationsArray.length > 0 
    ? sentNotificationsArray.reduce((sum, n) => sum + (n?.deliveryRate || 0), 0) / sentNotificationsArray.length 
    : 0;
  const averageOpenRate = sentNotificationsArray.length > 0 
    ? sentNotificationsArray.reduce((sum, n) => sum + (n?.openRate || 0), 0) / sentNotificationsArray.length 
    : 0;

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'success': return 'success';
      case 'warning': return 'warning';
      case 'error': return 'error';
      case 'emergency': return 'error';
      case 'info': return 'info';
      default: return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sent': return 'success';
      case 'scheduled': return 'info';
      case 'draft': return 'warning';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'error';
      case 'high': return 'warning';
      case 'normal': return 'info';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'success': return <CheckCircle />;
      case 'warning': return <Warning />;
      case 'error': return <Error />;
      case 'emergency': return <Emergency />;
      case 'info': return <Info />;
      default: return <Notifications />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleViewNotification = (notification: PushNotification) => {
    setSelectedNotification(notification);
    setDetailsDialogOpen(true);
  };

  const handleSettingsChange = (section: keyof NotificationSettings, key: string, value: any) => {
    setSettings(prev => {
      if (section === 'enabled') {
        return {
          ...prev,
          enabled: value,
        };
      }
      return {
        ...prev,
        [section]: {
          ...(prev[section] as object),
          [key]: value,
        },
      };
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Push Notifications
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Settings />}
            onClick={() => setSettingsDialogOpen(true)}
          >
            Settings
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setCreateDialogOpen(true)}
            sx={{ bgcolor: 'primary.main' }}
          >
            Create Notification
          </Button>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Notifications color="primary" sx={{ fontSize: 40 }} />
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  Total Notifications
                </Typography>
                <Typography variant="h5">
                  {totalNotifications}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  All time
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Send color="success" sx={{ fontSize: 40 }} />
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  Sent
                </Typography>
                <Typography variant="h5">
                  {sentNotifications}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Successfully delivered
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Schedule color="info" sx={{ fontSize: 40 }} />
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  Scheduled
                </Typography>
                <Typography variant="h5">
                  {scheduledNotifications}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Pending delivery
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <TrendingUp color="success" sx={{ fontSize: 40 }} />
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  Avg. Open Rate
                </Typography>
                <Typography variant="h5">
                  {averageOpenRate.toFixed(1)}%
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Last 30 days
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="All Notifications" icon={<Notifications />} />
          <Tab label="Templates" icon={<Campaign />} />
          <Tab label="Analytics" icon={<Analytics />} />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        {/* Filters */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search notifications..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
              sx={{ minWidth: 250 }}
            />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="sent">Sent</MenuItem>
                <MenuItem value="scheduled">Scheduled</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={typeFilter}
                label="Type"
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="info">Info</MenuItem>
                <MenuItem value="warning">Warning</MenuItem>
                <MenuItem value="error">Error</MenuItem>
                <MenuItem value="success">Success</MenuItem>
                <MenuItem value="emergency">Emergency</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('all');
                setTypeFilter('all');
              }}
            >
              Clear Filters
            </Button>
          </Box>
        </Paper>

        {/* Notifications Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Notification</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Target Audience</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Delivery Rate</TableCell>
                <TableCell>Open Rate</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredNotifications.map((notification) => (
                <TableRow key={notification.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      {getTypeIcon(notification.type)}
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {notification.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 300, display: 'block' }}>
                          {notification.message}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={notification.type}
                      color={getTypeColor(notification.type) as any}
                      size="small"
                      sx={{ textTransform: 'capitalize' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={notification.status}
                      color={getStatusColor(notification.status) as any}
                      size="small"
                      sx={{ textTransform: 'capitalize' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                      {notification.targetAudience.replace('-', ' ')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={notification.priority}
                      color={getPriorityColor(notification.priority) as any}
                      size="small"
                      sx={{ textTransform: 'capitalize' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {notification.deliveryRate > 0 ? `${notification.deliveryRate.toFixed(1)}%` : '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {notification.openRate > 0 ? `${notification.openRate.toFixed(1)}%` : '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(notification.createdAt)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      by {notification.createdBy}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleViewNotification(notification)}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small">
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Duplicate">
                        <IconButton size="small">
                          <Add />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          {templates.map((template) => (
            <Card key={template.id} sx={{ minWidth: 300, maxWidth: 400, flex: 1 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    {template.name}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Switch
                      checked={template.isActive}
                      size="small"
                    />
                    <IconButton size="small">
                      <Edit />
                    </IconButton>
                  </Box>
                </Box>
                
                <Chip
                  label={template.type}
                  color={getTypeColor(template.type) as any}
                  size="small"
                  sx={{ mb: 2, textTransform: 'capitalize' }}
                />
                
                <Typography variant="body2" gutterBottom fontWeight="medium">
                  {template.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {template.message}
                </Typography>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" gutterBottom>
                    Variables:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {template.variables.map((variable) => (
                      <Chip
                        key={variable}
                        label={`{${variable}}`}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    Used {template.usageCount} times
                  </Typography>
                  <Button size="small" variant="outlined">
                    Use Template
                  </Button>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
          <Card sx={{ minWidth: 300, flex: 1 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Delivery Performance
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Average Delivery Rate
                </Typography>
                <Typography variant="h4" color="primary">
                  {averageDeliveryRate.toFixed(1)}%
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Average Open Rate
                </Typography>
                <Typography variant="h4" color="secondary">
                  {averageOpenRate.toFixed(1)}%
                </Typography>
              </Box>
            </CardContent>
          </Card>
          
          <Card sx={{ minWidth: 300, flex: 1 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Notification Types
              </Typography>
              <List dense>
                {['info', 'warning', 'error', 'success', 'emergency'].map((type) => {
                  const count = notifications.filter(n => n.type === type).length;
                  const percentage = (count / totalNotifications * 100).toFixed(1);
                  return (
                    <ListItem key={type}>
                      <ListItemIcon>
                        {getTypeIcon(type)}
                      </ListItemIcon>
                      <ListItemText
                        primary={type.charAt(0).toUpperCase() + type.slice(1)}
                        secondary={`${count} notifications (${percentage}%)`}
                      />
                    </ListItem>
                  );
                })}
              </List>
            </CardContent>
          </Card>
          
          <Card sx={{ minWidth: 300, flex: 1 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Device Distribution
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <Smartphone />
                  </ListItemIcon>
                  <ListItemText
                    primary="Mobile"
                    secondary="85% of notifications"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Tablet />
                  </ListItemIcon>
                  <ListItemText
                    primary="Tablet"
                    secondary="45% of notifications"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Computer />
                  </ListItemIcon>
                  <ListItemText
                    primary="Desktop"
                    secondary="25% of notifications"
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Box>
      </TabPanel>

      {/* Notification Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Notification Details
        </DialogTitle>
        <DialogContent>
          {selectedNotification && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <Chip
                  label={selectedNotification.type}
                  color={getTypeColor(selectedNotification.type) as any}
                  sx={{ textTransform: 'capitalize' }}
                />
                <Chip
                  label={selectedNotification.status}
                  color={getStatusColor(selectedNotification.status) as any}
                  sx={{ textTransform: 'capitalize' }}
                />
                <Chip
                  label={selectedNotification.priority}
                  color={getPriorityColor(selectedNotification.priority) as any}
                  sx={{ textTransform: 'capitalize' }}
                />
              </Box>
              
              <Typography variant="h6" gutterBottom>
                {selectedNotification.title}
              </Typography>
              <Typography variant="body1" sx={{ mb: 3 }}>
                {selectedNotification.message}
              </Typography>
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Target Audience
                  </Typography>
                  <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                    {selectedNotification.targetAudience.replace('-', ' ')}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Device Types
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {selectedNotification.deviceTypes.map((device) => (
                      <Chip key={device} label={device} size="small" variant="outlined" />
                    ))}
                  </Box>
                </Box>
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Created By
                  </Typography>
                  <Typography variant="body2">
                    {selectedNotification.createdBy}
                  </Typography>
                </Box>
              </Box>
              
              {selectedNotification.status === 'sent' && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Delivery Rate
                    </Typography>
                    <Typography variant="h6" color="primary">
                      {selectedNotification.deliveryRate.toFixed(1)}%
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Open Rate
                    </Typography>
                    <Typography variant="h6" color="secondary">
                      {selectedNotification.openRate.toFixed(1)}%
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Click Rate
                    </Typography>
                    <Typography variant="h6" color="success.main">
                      {selectedNotification.clickRate.toFixed(1)}%
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
          <Button variant="outlined">Edit</Button>
          <Button variant="contained">Duplicate</Button>
        </DialogActions>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog
        open={settingsDialogOpen}
        onClose={() => setSettingsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Notification Settings
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6">General Settings</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.enabled}
                      onChange={(e) => handleSettingsChange('enabled' as any, '', e.target.checked)}
                    />
                  }
                  label="Enable Push Notifications"
                />
              </AccordionDetails>
            </Accordion>
            
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6">Quiet Hours</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.quietHours.enabled}
                      onChange={(e) => handleSettingsChange('quietHours', 'enabled', e.target.checked)}
                    />
                  }
                  label="Enable Quiet Hours"
                  sx={{ mb: 2 }}
                />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    label="Start Time"
                    type="time"
                    value={settings.quietHours.startTime}
                    onChange={(e) => handleSettingsChange('quietHours', 'startTime', e.target.value)}
                    disabled={!settings.quietHours.enabled}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label="End Time"
                    type="time"
                    value={settings.quietHours.endTime}
                    onChange={(e) => handleSettingsChange('quietHours', 'endTime', e.target.value)}
                    disabled={!settings.quietHours.enabled}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
              </AccordionDetails>
            </Accordion>
            
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6">Device Settings</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={settings.deviceSettings.mobile}
                        onChange={(e) => handleSettingsChange('deviceSettings', 'mobile', e.target.checked)}
                      />
                    }
                    label="Mobile Devices"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={settings.deviceSettings.tablet}
                        onChange={(e) => handleSettingsChange('deviceSettings', 'tablet', e.target.checked)}
                      />
                    }
                    label="Tablet Devices"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={settings.deviceSettings.desktop}
                        onChange={(e) => handleSettingsChange('deviceSettings', 'desktop', e.target.checked)}
                      />
                    }
                    label="Desktop Devices"
                  />
                </FormGroup>
              </AccordionDetails>
            </Accordion>
            
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6">Category Settings</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <FormGroup>
                  {Object.entries(settings.categorySettings).map(([category, enabled]) => (
                    <FormControlLabel
                      key={category}
                      control={
                        <Checkbox
                          checked={enabled}
                          onChange={(e) => handleSettingsChange('categorySettings', category, e.target.checked)}
                        />
                      }
                      label={category.charAt(0).toUpperCase() + category.slice(1)}
                    />
                  ))}
                </FormGroup>
              </AccordionDetails>
            </Accordion>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsDialogOpen(false)}>Cancel</Button>
          <Button variant="contained">Save Settings</Button>
        </DialogActions>
      </Dialog>

      {/* Create Notification Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Create New Notification
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              label="Title"
              fullWidth
              placeholder="Enter notification title"
            />
            <TextField
              label="Message"
              fullWidth
              multiline
              rows={3}
              placeholder="Enter notification message"
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select label="Type" defaultValue="info">
                  <MenuItem value="info">Info</MenuItem>
                  <MenuItem value="warning">Warning</MenuItem>
                  <MenuItem value="error">Error</MenuItem>
                  <MenuItem value="success">Success</MenuItem>
                  <MenuItem value="emergency">Emergency</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select label="Priority" defaultValue="normal">
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="normal">Normal</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="urgent">Urgent</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <FormControl fullWidth>
              <InputLabel>Target Audience</InputLabel>
              <Select label="Target Audience" defaultValue="all">
                <MenuItem value="all">All Users</MenuItem>
                <MenuItem value="project-managers">Project Managers</MenuItem>
                <MenuItem value="field-workers">Field Workers</MenuItem>
                <MenuItem value="supervisors">Supervisors</MenuItem>
                <MenuItem value="custom">Custom Selection</MenuItem>
              </Select>
            </FormControl>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Device Types
              </Typography>
              <FormGroup row>
                <FormControlLabel control={<Checkbox defaultChecked />} label="Mobile" />
                <FormControlLabel control={<Checkbox defaultChecked />} label="Tablet" />
                <FormControlLabel control={<Checkbox />} label="Desktop" />
              </FormGroup>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button variant="outlined">Save as Draft</Button>
          <Button variant="contained">Send Now</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PushNotifications;